import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getBusinessProfileDefaults, getIndustryDefaults, getTargetAudienceSuggestions, generateSEOKeywords } from '../utils/businessProfileDefaults';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
    ExpandMore as ExpandMoreIcon,
    Business,
    Settings
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axiosWithAuth from '../utils/axiosWithAuth';
import { isCASOnlySubscription } from '../utils/subscriptionUtils';

const BusinessProfilePage = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const subscriptionId = searchParams.get('subscriptionId');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [subscription, setSubscription] = useState(null);
    const [businessProfile, setBusinessProfile] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // Form state with intelligent defaults
    const [formData, setFormData] = useState(() => {
        const defaults = getBusinessProfileDefaults();
        return {
            // Basic fields
            entity_name: '',
            business_description: '',
            services_products_description: '',
            website_url: '',
            target_audience: '',
            contact_info: {
                email: '',
                phone: '',
                address: ''
            },

            // Advanced fields with intelligent defaults
            key_value_propositions: defaults.key_value_propositions.join(', '),
            brand_attributes: defaults.brand_attributes.join(', '),
            secondary_call_to_actions: defaults.secondary_call_to_actions.join(', '),
            text_emotion_keywords: defaults.text_emotion_keywords.join(', '),
            image_dominant_colors: defaults.image_dominant_colors.join(', '),
            negative_image_prompts: defaults.negative_image_prompts.join(', '),
            content_to_exclude: defaults.content_to_exclude.join(', '),
            homepage_section_order_preference: defaults.homepage_section_order_preference.join(', '),
            seo_primary_keywords: '',
            seo_secondary_keywords: '',
            text_voice: defaults.text_voice,
            text_formality: defaults.text_formality,
            image_art_style: defaults.image_art_style,
            image_composition_preference: defaults.image_composition_preference,
            website_style_preference: defaults.website_style_preference,
            primary_call_to_action: defaults.primary_call_to_action,
            image_generation_required: defaults.image_generation_required,
            use_default_placeholder_images: defaults.use_default_placeholder_images,
            page_generation_mode: defaults.page_generation_mode,
            blog_generation_mode: defaults.blog_generation_mode,

            // Additional fields from defaults
            industry_type: '',
            target_language: defaults.target_language,
            preferred_content_length: defaults.preferred_content_length,
            content_update_frequency: defaults.content_update_frequency,
            social_media_links: defaults.social_media_links.join(', '),
            logo_url: '',
            requested_pages: defaults.requested_pages.join(', '),
            requested_blog_topics: defaults.requested_blog_topics.join(', '),
            default_banner_placeholder_url: '',
            default_content_placeholder_url: ''
        };
    });

    useEffect(() => {
        if (subscriptionId) {
            fetchSubscriptionAndProfile();
        } else {
            setLoading(false);
            setError('No subscription ID provided');
        }
    }, [subscriptionId]);

    const fetchSubscriptionAndProfile = async () => {
        try {
            setLoading(true);

            // Fetch subscription details
            const subscriptionResponse = await axiosWithAuth.get(`/subscriptions/${subscriptionId}`);
            const subscriptionData = subscriptionResponse.data;
            setSubscription(subscriptionData);

            // Check if this is a CAS-only subscription
            if (isCASOnlySubscription(subscriptionData)) {
                setError('Business profiles are not required for CAS-only subscriptions. All CAS operations are self-contained.');
                setLoading(false);
                return;
            }

            // Fetch existing business profile if it exists
            try {
                const profileResponse = await axiosWithAuth.get(`/subscriptions/${subscriptionId}/business-profile`);
                const profileData = profileResponse.data;
                if (profileData) {
                    setBusinessProfile(profileData);
                    setIsEditMode(true);

                    // Populate form with existing data
                    setFormData({
                        ...formData,
                        entity_name: profileData.entity_name || '',
                        business_description: profileData.business_description || '',
                        services_products_description: profileData.services_products_description || '',
                        website_url: profileData.website_url || '',
                        target_audience: profileData.target_audience || '',
                        contact_info: {
                            email: profileData.contact_email || '',
                            phone: profileData.contact_phone || '',
                            address: profileData.contact_address || ''
                        },
                        key_value_propositions: profileData.key_value_propositions?.join(', ') || formData.key_value_propositions,
                        brand_attributes: profileData.brand_attributes?.join(', ') || formData.brand_attributes,
                        secondary_call_to_actions: profileData.secondary_call_to_actions?.join(', ') || formData.secondary_call_to_actions,
                        text_emotion_keywords: profileData.text_emotion_keywords?.join(', ') || formData.text_emotion_keywords,
                        image_dominant_colors: profileData.image_dominant_colors?.join(', ') || formData.image_dominant_colors,
                        negative_image_prompts: profileData.negative_image_prompts?.join(', ') || formData.negative_image_prompts,
                        content_to_exclude: profileData.content_to_exclude?.join(', ') || formData.content_to_exclude,
                        homepage_section_order_preference: profileData.homepage_section_order_preference?.join(', ') || formData.homepage_section_order_preference,
                        seo_primary_keywords: profileData.seo_primary_keywords || '',
                        seo_secondary_keywords: profileData.seo_secondary_keywords || '',
                        text_voice: profileData.text_voice || formData.text_voice,
                        text_formality: profileData.text_formality || formData.text_formality,
                        image_art_style: profileData.image_art_style || formData.image_art_style,
                        image_composition_preference: profileData.image_composition_preference || formData.image_composition_preference,
                        website_style_preference: profileData.website_style_preference || formData.website_style_preference,
                        primary_call_to_action: profileData.primary_call_to_action || formData.primary_call_to_action,
                        image_generation_required: profileData.image_generation_required ?? formData.image_generation_required,
                        use_default_placeholder_images: profileData.use_default_placeholder_images ?? formData.use_default_placeholder_images,
                        page_generation_mode: profileData.page_generation_mode || formData.page_generation_mode,
                        blog_generation_mode: profileData.blog_generation_mode || formData.blog_generation_mode,

                        // Additional fields
                        industry_type: profileData.industry_type || '',
                        target_language: profileData.target_language || formData.target_language,
                        preferred_content_length: profileData.preferred_content_length || formData.preferred_content_length,
                        content_update_frequency: profileData.content_update_frequency || formData.content_update_frequency,
                        social_media_links: profileData.social_media_links?.join(', ') || formData.social_media_links,
                        logo_url: profileData.logo_url || '',
                        requested_pages: profileData.requested_pages?.join(', ') || formData.requested_pages,
                        requested_blog_topics: profileData.requested_blog_topics?.join(', ') || formData.requested_blog_topics,
                        default_banner_placeholder_url: profileData.default_banner_placeholder_url || '',
                        default_content_placeholder_url: profileData.default_content_placeholder_url || ''
                    });
                }
            } catch (profileError) {
                // Profile doesn't exist, this is expected for new subscriptions
            }
        } catch (error) {
            setError('Failed to fetch subscription details: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            // Prepare data for API
            const requestData = {
                entity_name: formData.entity_name,
                business_description: formData.business_description,
                services_products_description: formData.services_products_description,
                website_url: formData.website_url,
                target_audience: formData.target_audience,
                contact_email: formData.contact_info.email,
                contact_phone: formData.contact_info.phone,
                contact_address: formData.contact_info.address,
                key_value_propositions: formData.key_value_propositions.split(',').map(item => item.trim()).filter(item => item),
                brand_attributes: formData.brand_attributes.split(',').map(item => item.trim()).filter(item => item),
                secondary_call_to_actions: formData.secondary_call_to_actions.split(',').map(item => item.trim()).filter(item => item),
                text_emotion_keywords: formData.text_emotion_keywords.split(',').map(item => item.trim()).filter(item => item),
                image_dominant_colors: formData.image_dominant_colors.split(',').map(item => item.trim()).filter(item => item),
                negative_image_prompts: formData.negative_image_prompts.split(',').map(item => item.trim()).filter(item => item),
                content_to_exclude: formData.content_to_exclude.split(',').map(item => item.trim()).filter(item => item),
                homepage_section_order_preference: formData.homepage_section_order_preference.split(',').map(item => item.trim()).filter(item => item),
                seo_primary_keywords: formData.seo_primary_keywords.split(',').map(item => item.trim()).filter(item => item),
                seo_secondary_keywords: formData.seo_secondary_keywords.split(',').map(item => item.trim()).filter(item => item),
                text_voice: formData.text_voice,
                text_formality: formData.text_formality,
                image_art_style: formData.image_art_style,
                image_composition_preference: formData.image_composition_preference,
                website_style_preference: formData.website_style_preference,
                primary_call_to_action: formData.primary_call_to_action,
                image_generation_required: formData.image_generation_required,
                use_default_placeholder_images: formData.use_default_placeholder_images,
                page_generation_mode: formData.page_generation_mode,
                blog_generation_mode: formData.blog_generation_mode,

                // Additional fields
                industry_type: formData.industry_type,
                target_language: formData.target_language,
                preferred_content_length: formData.preferred_content_length,
                content_update_frequency: formData.content_update_frequency,
                social_media_links: formData.social_media_links.split(',').map(item => item.trim()).filter(item => item),
                logo_url: formData.logo_url,
                requested_pages: formData.requested_pages.split(',').map(item => item.trim()).filter(item => item),
                requested_blog_topics: formData.requested_blog_topics.split(',').map(item => item.trim()).filter(item => item),
                default_banner_placeholder_url: formData.default_banner_placeholder_url,
                default_content_placeholder_url: formData.default_content_placeholder_url
            };


            if (isEditMode && businessProfile) {
                // Update existing profile
                await axiosWithAuth.put(`/subscriptions/${subscriptionId}/business-profile`, requestData);
                setSuccess('Business profile updated successfully!');
            } else {
                // Create new profile
                await axiosWithAuth.post(`/subscriptions/${subscriptionId}/business-profile`, requestData);
                setSuccess('Business profile created successfully!');
                setIsEditMode(true);
            }

            // Refresh the profile data
            await fetchSubscriptionAndProfile();
        } catch (error) {
            setError('Failed to save business profile: ' + (error.response?.data?.detail || error.message));
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }

        // Auto-fill logic for better user experience
        if (field === 'industry_type' && value) {
            handleIndustryChange(value);
        } else if (field === 'business_description' && value) {
            handleBusinessDescriptionChange(value);
        }
    };

    // Handle industry change to update defaults
    const handleIndustryChange = (industryType) => {
        const industryDefaults = getIndustryDefaults(industryType);
        const targetAudience = getTargetAudienceSuggestions(industryType);

        setFormData(prev => ({
            ...prev,
            industry_type: industryType,
            target_audience: prev.target_audience || targetAudience,
            website_style_preference: industryDefaults.website_style_preference,
            brand_attributes: industryDefaults.brand_attributes.join(', '),
            primary_call_to_action: industryDefaults.primary_call_to_action,
            secondary_call_to_actions: industryDefaults.secondary_call_to_actions.join(', '),
            seo_primary_keywords: industryDefaults.seo_primary_keywords.join(', '),
            seo_secondary_keywords: industryDefaults.seo_secondary_keywords.join(', '),
            text_emotion_keywords: industryDefaults.text_emotion_keywords.join(', '),
            image_composition_preference: industryDefaults.image_composition_preference,
            content_to_exclude: industryDefaults.content_to_exclude.join(', ')
        }));
    };

    // Auto-generate SEO keywords when business description changes
    const handleBusinessDescriptionChange = (description) => {
        setFormData(prev => {
            const newKeywords = generateSEOKeywords(description, prev.industry_type);
            return {
                ...prev,
                business_description: description,
                // Only auto-populate if SEO fields are empty
                seo_primary_keywords: prev.seo_primary_keywords || newKeywords.slice(0, 3).join(', '),
                seo_secondary_keywords: prev.seo_secondary_keywords || newKeywords.slice(3, 6).join(', ')
            };
        });
    };

    // Helper function to show auto-filled indicator
    const getAutoFilledHelperText = (fieldName) => {
        if (!isEditMode && formData[fieldName] && formData[fieldName] !== '') {
            return 'Auto-filled based on your industry selection';
        }
        return '';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error && !subscription) {
        return (
            <Box sx={{ flexGrow: 1 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => window.history.back()}
                >
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box display="flex" alignItems="center" mb={3}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => window.history.back()}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h4" gutterBottom>
                    {isEditMode ? 'Edit Business Profile' : 'Create Business Profile'}
                </Typography>
            </Box>

            {subscription && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Subscription: {subscription.subscription_name || subscription.plan?.name || 'Unnamed Subscription'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {subscription.subscription_name && `Plan: ${subscription.plan?.name} • `}Status: {subscription.status}
                    </Typography>
                </Paper>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {/* Auto-fill Information */}
            {!isEditMode && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Smart Auto-Fill:</strong> Start by filling out your business description and selecting your industry type.
                        The form will automatically populate many fields with intelligent defaults based on your industry and business description.
                    </Typography>
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Basic Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Business/Entity Name"
                                value={formData.entity_name}
                                onChange={(e) => handleInputChange('entity_name', e.target.value)}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Website URL (optional)"
                                value={formData.website_url}
                                onChange={(e) => handleInputChange('website_url', e.target.value)}
                                placeholder="https://example.com"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Business Description"
                                multiline
                                rows={3}
                                value={formData.business_description}
                                onChange={(e) => handleInputChange('business_description', e.target.value)}
                                required
                                placeholder="Describe what your business does, your mission, and what makes you unique..."
                                helperText="This will auto-generate SEO keywords when you fill it out"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Services/Products Description"
                                multiline
                                rows={3}
                                value={formData.services_products_description}
                                onChange={(e) => handleInputChange('services_products_description', e.target.value)}
                                required
                                placeholder="Describe your main services or products in detail..."
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Target Audience"
                                value={formData.target_audience}
                                onChange={(e) => handleInputChange('target_audience', e.target.value)}
                                placeholder="Who are your ideal customers? Describe their demographics, interests, and needs..."
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Industry Type</InputLabel>
                                <Select
                                    value={formData.industry_type}
                                    onChange={(e) => handleInputChange('industry_type', e.target.value)}
                                    label="Industry Type"
                                >
                                    <MenuItem value="">
                                        <em>Select your industry (auto-populates defaults)</em>
                                    </MenuItem>
                                    <MenuItem value="technology">Technology & Software</MenuItem>
                                    <MenuItem value="healthcare">Healthcare & Medical</MenuItem>
                                    <MenuItem value="finance">Finance & Investment</MenuItem>
                                    <MenuItem value="retail">Retail & E-commerce</MenuItem>
                                    <MenuItem value="consulting">Business Consulting</MenuItem>
                                    <MenuItem value="education">Education & Training</MenuItem>
                                    <MenuItem value="real_estate">Real Estate</MenuItem>
                                    <MenuItem value="legal">Legal Services</MenuItem>
                                    <MenuItem value="marketing">Marketing & Advertising</MenuItem>
                                    <MenuItem value="manufacturing">Manufacturing</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Target Language</InputLabel>
                                <Select
                                    value={formData.target_language}
                                    onChange={(e) => handleInputChange('target_language', e.target.value)}
                                    label="Target Language"
                                >
                                    <MenuItem value="en">English</MenuItem>
                                    <MenuItem value="es">Spanish</MenuItem>
                                    <MenuItem value="fr">French</MenuItem>
                                    <MenuItem value="de">German</MenuItem>
                                    <MenuItem value="it">Italian</MenuItem>
                                    <MenuItem value="pt">Portuguese</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Contact Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Contact Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.contact_info.email}
                                onChange={(e) => handleInputChange('contact_info.email', e.target.value)}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Phone"
                                value={formData.contact_info.phone}
                                onChange={(e) => handleInputChange('contact_info.phone', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Address"
                                value={formData.contact_info.address}
                                onChange={(e) => handleInputChange('contact_info.address', e.target.value)}
                            />
                        </Grid>

                        {/* Advanced Settings */}
                        <Grid item xs={12}>
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">
                                        Advanced Settings
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Text Voice</InputLabel>
                                                <Select
                                                    value={formData.text_voice}
                                                    onChange={(e) => handleInputChange('text_voice', e.target.value)}
                                                    label="Text Voice"
                                                >
                                                    <MenuItem value="First Person (I/We)">First Person (I/We)</MenuItem>
                                                    <MenuItem value="Third Person (They/The Company)">Third Person (They/The Company)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Text Formality</InputLabel>
                                                <Select
                                                    value={formData.text_formality}
                                                    onChange={(e) => handleInputChange('text_formality', e.target.value)}
                                                    label="Text Formality"
                                                >
                                                    <MenuItem value="Very Formal">Very Formal</MenuItem>
                                                    <MenuItem value="Formal">Formal</MenuItem>
                                                    <MenuItem value="Neutral">Neutral</MenuItem>
                                                    <MenuItem value="Informal">Informal</MenuItem>
                                                    <MenuItem value="Very Informal/Conversational">Very Informal/Conversational</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Image Art Style</InputLabel>
                                                <Select
                                                    value={formData.image_art_style}
                                                    onChange={(e) => handleInputChange('image_art_style', e.target.value)}
                                                    label="Image Art Style"
                                                >
                                                    <MenuItem value="photorealistic">Photorealistic</MenuItem>
                                                    <MenuItem value="illustration_flat">Illustration Flat</MenuItem>
                                                    <MenuItem value="illustration_3d_cartoon">Illustration 3D Cartoon</MenuItem>
                                                    <MenuItem value="abstract_geometric">Abstract Geometric</MenuItem>
                                                    <MenuItem value="watercolor">Watercolor</MenuItem>
                                                    <MenuItem value="pixel_art">Pixel Art</MenuItem>
                                                    <MenuItem value="dark_moody">Dark Moody</MenuItem>
                                                    <MenuItem value="light_airy">Light Airy</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Primary Call to Action"
                                                value={formData.primary_call_to_action}
                                                onChange={(e) => handleInputChange('primary_call_to_action', e.target.value)}
                                                placeholder="e.g., 'Get Started Today', 'Contact Us', 'Learn More'"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Key Value Propositions"
                                                value={formData.key_value_propositions}
                                                onChange={(e) => handleInputChange('key_value_propositions', e.target.value)}
                                                placeholder="Enter key value propositions, separated by commas"
                                                helperText={getAutoFilledHelperText('key_value_propositions') || "e.g., Innovation, Quality, Customer Service"}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Brand Attributes"
                                                value={formData.brand_attributes}
                                                onChange={(e) => handleInputChange('brand_attributes', e.target.value)}
                                                placeholder="Enter brand attributes, separated by commas"
                                                helperText={getAutoFilledHelperText('brand_attributes') || "e.g., Professional, Trustworthy, Modern"}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Secondary Call to Actions"
                                                value={formData.secondary_call_to_actions}
                                                onChange={(e) => handleInputChange('secondary_call_to_actions', e.target.value)}
                                                placeholder="Enter secondary call to actions, separated by commas"
                                                helperText="e.g., Download Brochure, Schedule Demo"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Text Emotion Keywords"
                                                value={formData.text_emotion_keywords}
                                                onChange={(e) => handleInputChange('text_emotion_keywords', e.target.value)}
                                                placeholder="Enter text emotion keywords, separated by commas"
                                                helperText="e.g., Confident, Reliable, Professional"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Image Dominant Colors"
                                                value={formData.image_dominant_colors}
                                                onChange={(e) => handleInputChange('image_dominant_colors', e.target.value)}
                                                placeholder="Enter dominant colors, separated by commas"
                                                helperText="e.g., Blue, White, Gray"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Negative Image Prompts"
                                                value={formData.negative_image_prompts}
                                                onChange={(e) => handleInputChange('negative_image_prompts', e.target.value)}
                                                placeholder="Enter negative image prompts, separated by commas"
                                                helperText="What to avoid in generated images"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Content to Exclude"
                                                value={formData.content_to_exclude}
                                                onChange={(e) => handleInputChange('content_to_exclude', e.target.value)}
                                                placeholder="Enter content to exclude, separated by commas"
                                                helperText="Topics or content types to avoid"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Homepage Section Order Preference"
                                                value={formData.homepage_section_order_preference}
                                                onChange={(e) => handleInputChange('homepage_section_order_preference', e.target.value)}
                                                placeholder="Enter homepage section order, separated by commas"
                                                helperText="e.g., Hero, About, Services, CTA"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Website Style Preference"
                                                multiline
                                                rows={2}
                                                value={formData.website_style_preference}
                                                onChange={(e) => handleInputChange('website_style_preference', e.target.value)}
                                                placeholder="Describe the overall look and feel you want for your website..."
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="SEO Primary Keywords"
                                                value={formData.seo_primary_keywords}
                                                onChange={(e) => handleInputChange('seo_primary_keywords', e.target.value)}
                                                placeholder="Enter primary keywords for SEO, separated by commas"
                                                helperText={getAutoFilledHelperText('seo_primary_keywords') || "Auto-generated from your business description"}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="SEO Secondary Keywords"
                                                value={formData.seo_secondary_keywords}
                                                onChange={(e) => handleInputChange('seo_secondary_keywords', e.target.value)}
                                                placeholder="Enter secondary keywords for SEO, separated by commas"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.image_generation_required}
                                                        onChange={(e) => handleInputChange('image_generation_required', e.target.checked)}
                                                    />
                                                }
                                                label="Generate Images"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.generate_blogs}
                                                        onChange={(e) => handleInputChange('generate_blogs', e.target.checked)}
                                                    />
                                                }
                                                label="Generate Blog Posts"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Preferred Content Length</InputLabel>
                                                <Select
                                                    value={formData.preferred_content_length}
                                                    onChange={(e) => handleInputChange('preferred_content_length', e.target.value)}
                                                    label="Preferred Content Length"
                                                >
                                                    <MenuItem value="short">Short</MenuItem>
                                                    <MenuItem value="medium">Medium</MenuItem>
                                                    <MenuItem value="long">Long</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Content Update Frequency</InputLabel>
                                                <Select
                                                    value={formData.content_update_frequency}
                                                    onChange={(e) => handleInputChange('content_update_frequency', e.target.value)}
                                                    label="Content Update Frequency"
                                                >
                                                    <MenuItem value="weekly">Weekly</MenuItem>
                                                    <MenuItem value="monthly">Monthly</MenuItem>
                                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Logo URL"
                                                value={formData.logo_url}
                                                onChange={(e) => handleInputChange('logo_url', e.target.value)}
                                                placeholder="https://example.com/logo.png"
                                                helperText="URL to your existing logo"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Social Media Links"
                                                value={formData.social_media_links}
                                                onChange={(e) => handleInputChange('social_media_links', e.target.value)}
                                                placeholder="https://linkedin.com/company/example, https://twitter.com/example"
                                                helperText="Enter social media URLs, separated by commas"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Default Pages"
                                                value={formData.requested_pages}
                                                onChange={(e) => handleInputChange('requested_pages', e.target.value)}
                                                placeholder="About, Services, Contact, Portfolio"
                                                helperText="Enter default pages to generate, separated by commas"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Default Blog Topics"
                                                value={formData.requested_blog_topics}
                                                onChange={(e) => handleInputChange('requested_blog_topics', e.target.value)}
                                                placeholder="Industry Trends, Best Practices, Case Studies"
                                                helperText="Enter default blog topics, separated by commas"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Custom Banner Placeholder URL"
                                                value={formData.default_banner_placeholder_url}
                                                onChange={(e) => handleInputChange('default_banner_placeholder_url', e.target.value)}
                                                placeholder="https://example.com/banner-placeholder.jpg"
                                                helperText="URL for custom banner placeholder"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Custom Content Placeholder URL"
                                                value={formData.default_content_placeholder_url}
                                                onChange={(e) => handleInputChange('default_content_placeholder_url', e.target.value)}
                                                placeholder="https://example.com/content-placeholder.jpg"
                                                helperText="URL for custom content placeholder"
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : (isEditMode ? 'Update Profile' : 'Create Profile')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => window.history.back()}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default BusinessProfilePage; 